import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { GET_DASHBOARD_STATS, GET_IMAGE_HEALTH_SCORES } from '../graphql/queries';
import { DataTable } from '../components/DataTable';
import OsIcon, { PackerBuildIcon } from '../components/OsIcon';
import { scheduleBlueprintLabel, scheduleBlueprintIcon } from '../utils/os-icons';

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function phaseBadge(phase) {
  const map = { Succeeded: 'success', Running: 'info', Failed: 'error', Pending: 'pending' };
  return <span className={`badge badge-${map[phase] || 'pending'}`}>{phase}</span>;
}

const recentBuildColumns = [
  {
    accessorKey: 'name',
    header: 'Workflow',
    cell: ({ row }) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <PackerBuildIcon size={16} />
        <OsIcon name={row.original.name} size={16} />
        <Link to={`/build/${row.original.name}`} className="link">{row.original.name}</Link>
      </span>
    ),
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ getValue }) => phaseBadge(getValue()),
  },
  {
    accessorKey: 'startedAt',
    header: 'Started',
    cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : '-',
  },
  {
    accessorKey: 'finishedAt',
    header: 'Finished',
    cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : '-',
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => {
      const { startedAt, finishedAt } = row.original;
      if (!startedAt || !finishedAt) return '-';
      const ms = new Date(finishedAt) - new Date(startedAt);
      const mins = Math.floor(ms / 60000);
      const secs = Math.round((ms % 60000) / 1000);
      return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
    },
  },
];

const PIE_COLORS = ['#00b894', '#d63031', '#0984e3', '#fdcb6e'];

export default function Dashboard() {
  const { data, loading } = useQuery(GET_DASHBOARD_STATS, { pollInterval: 30000 });
  const { data: healthData } = useQuery(GET_IMAGE_HEALTH_SCORES, { variables: { limit: 20 } });

  const stats = data?.getDashboardStats || {
    totalImages: 0,
    activeImages: 0,
    failedBuilds: 0,
    scheduledBlueprints: 0,
    criticalVulnerabilities: 0,
    buildSuccessRate: 0,
    deprecatedCount: 0,
    recentBuilds: [],
    recentImages: [],
    upcomingSchedules: [],
  };

  // Derive chart data and build stats from recent builds (Argo Workflows API)
  const buildsByPhase = useMemo(() => {
    const builds = stats.recentBuilds || [];
    const counts = {};
    for (const b of builds) {
      const phase = b.phase || 'Unknown';
      counts[phase] = (counts[phase] || 0) + 1;
    }
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [stats.recentBuilds]);

  // Compute failed builds and success rate from recentBuilds (consistent with charts)
  const failedBuilds = useMemo(() => {
    return (stats.recentBuilds || []).filter(b => b.phase === 'Failed').length;
  }, [stats.recentBuilds]);

  const buildSuccessRate = useMemo(() => {
    const builds = stats.recentBuilds || [];
    if (builds.length === 0) return 0;
    const succeeded = builds.filter(b => b.phase === 'Succeeded').length;
    return Math.round((succeeded / builds.length) * 100);
  }, [stats.recentBuilds]);

  // Average build duration (from completed builds)
  const avgDuration = useMemo(() => {
    const builds = stats.recentBuilds || [];
    const durations = builds
      .filter(b => b.startedAt && b.finishedAt)
      .map(b => new Date(b.finishedAt) - new Date(b.startedAt));
    if (durations.length === 0) return null;
    const avgMs = durations.reduce((a, b) => a + b, 0) / durations.length;
    const mins = Math.floor(avgMs / 60000);
    const secs = Math.round((avgMs % 60000) / 1000);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  }, [stats.recentBuilds]);

  // Builds per day (last 7 days)
  const buildsPerDay = useMemo(() => {
    const builds = stats.recentBuilds || [];
    const days = {};
    for (const b of builds) {
      if (!b.startedAt) continue;
      const day = new Date(b.startedAt).toLocaleDateString('en-US', { weekday: 'short' });
      days[day] = (days[day] || 0) + 1;
    }
    return Object.entries(days).map(([day, count]) => ({ day, builds: count }));
  }, [stats.recentBuilds]);

  return (
    <div>
      <h2 className="page-title">Dashboard</h2>

      <div className="card">
        <p>Welcome to the Image Factory. This platform automates the creation, hardening, security scanning, and distribution of standardized machine images.</p>
      </div>

      <div className="stats-grid">
        <StatCard label="Total Images" value={loading ? '...' : stats.totalImages} color="#0984e3" />
        <StatCard label="Active Images" value={loading ? '...' : stats.activeImages} color="#00b894" />
        <StatCard label="Failed Builds" value={loading ? '...' : failedBuilds} color="#d63031" />
        <StatCard label="Build Success Rate" value={loading ? '...' : `${buildSuccessRate}%`} color="#00cec9" />
        <StatCard label="Scheduled Blueprints" value={loading ? '...' : stats.scheduledBlueprints} color="#6c5ce7" />
        <StatCard label="Critical CVEs" value={loading ? '...' : stats.criticalVulnerabilities} color="#e17055" />
      </div>

      {(buildsPerDay.length > 0 || buildsByPhase.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
          {buildsPerDay.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 16 }}>Builds per Day</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={buildsPerDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="builds" fill="#0984e3" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {buildsByPhase.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 16 }}>Build Status</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={buildsByPhase} cx="50%" cy="45%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey="value">
                    {buildsByPhase.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" iconType="circle" formatter={(name, entry) => `${name}: ${entry.payload.value}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {avgDuration && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, color: '#636e72' }}>Average Build Duration:</span>
            <span style={{ fontSize: 20, fontWeight: 600 }}>{avgDuration}</span>
          </div>
        </div>
      )}

      {stats.recentBuilds?.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>Recent Builds</h3>
          <DataTable columns={recentBuildColumns} data={stats.recentBuilds} pageSize={5} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {stats.recentImages?.length > 0 && (
          <div className="card">
            <h3 style={{ marginBottom: 12 }}>Recent Images</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>OS</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentImages.map((img) => (
                  <tr key={img.id}>
                    <td><Link to={`/images/${img.id}`} className="link">{img.name}</Link></td>
                    <td><OsIcon name={img.blueprintName || img.osType} showLabel size={16} /></td>
                    <td><span className={`badge badge-${img.status === 'completed' ? 'success' : img.status === 'failed' ? 'error' : 'pending'}`}>{img.status}</span></td>
                    <td>{img.createdAt ? new Date(img.createdAt).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8 }}>
              <Link to="/images" className="link" style={{ fontSize: 13 }}>View all images</Link>
            </div>
          </div>
        )}

        {stats.upcomingSchedules?.length > 0 && (
          <div className="card">
            <h3 style={{ marginBottom: 12 }}>Active Schedules</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Blueprint</th>
                  <th>Schedule</th>
                  <th>Last Run</th>
                </tr>
              </thead>
              <tbody>
                {stats.upcomingSchedules.map((s) => (
                  <tr key={s.name}>
                    <td>{s.name}</td>
                    <td>{s.blueprintName
                      ? <OsIcon name={s.blueprintName} showLabel size={16} />
                      : (() => {
                          const Icon = scheduleBlueprintIcon(s.name);
                          const label = scheduleBlueprintLabel(s.name);
                          if (!label) return <span style={{ color: '#636e72' }}>{'\u2014'}</span>;
                          return (
                            <span className="inline-flex items-center gap-1.5" style={{ color: '#636e72' }} title={label}>
                              {Icon && <Icon width={16} height={16} aria-hidden="true" />}
                              <span>{label}</span>
                            </span>
                          );
                        })()}</td>
                    <td><code style={{ fontSize: 12 }}>{s.schedule}</code></td>
                    <td>{s.lastRun ? new Date(s.lastRun).toLocaleDateString() : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8 }}>
              <Link to="/schedules" className="link" style={{ fontSize: 13 }}>Manage schedules</Link>
            </div>
          </div>
        )}
      </div>

      {stats.deprecatedCount > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid #fdcb6e' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong style={{ color: '#fdcb6e' }}>Lifecycle Warning</strong>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: '#636e72' }}>
                {stats.deprecatedCount} deprecated image{stats.deprecatedCount !== 1 ? 's' : ''} found. Review and clean up to free resources.
              </p>
            </div>
            <Link to="/lifecycle" className="btn btn-primary" style={{ background: '#fdcb6e', color: '#2d3436', fontSize: 13 }}>
              Review
            </Link>
          </div>
        </div>
      )}

      {healthData?.listImageHealthScores?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3>Fleet Health</h3>
          </div>
          {(() => {
            const scores = healthData.listImageHealthScores;
            const avg = scores.length > 0 ? Math.round(scores.reduce((s, h) => s + h.overallScore, 0) / scores.length) : 0;
            const gradeColor = { A: '#00b894', B: '#00cec9', C: '#fdcb6e', D: '#e17055', F: '#d63031' };
            const avgGrade = avg >= 90 ? 'A' : avg >= 80 ? 'B' : avg >= 70 ? 'C' : avg >= 60 ? 'D' : 'F';
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                  <div style={{ fontSize: 36, fontWeight: 700, color: gradeColor[avgGrade] }}>{avgGrade}</div>
                  <div>
                    <div style={{ fontSize: 14, color: '#636e72' }}>Average health score: <strong>{avg}/100</strong></div>
                    <div style={{ fontSize: 12, color: '#b2bec3' }}>Across {scores.length} images</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {scores.slice(0, 8).map(s => (
                    <Link key={s.imageId} to={`/scorecard/${s.imageId}`} style={{ textDecoration: 'none' }}>
                      <span className="badge" style={{ background: (gradeColor[s.grade] || '#636e72') + '22', color: gradeColor[s.grade] || '#636e72', fontSize: 11 }}>
                        {s.imageName}: {s.grade}
                      </span>
                    </Link>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      <div className="card">
        <h3>Quick Actions</h3>
        <div className="actions" style={{ flexWrap: 'wrap' }}>
          <Link to="/build" className="btn btn-primary">Trigger Build</Link>
          <Link to="/images" className="btn btn-primary" style={{ background: '#00b894' }}>View Images</Link>
          <Link to="/blueprints" className="btn btn-primary" style={{ background: '#6c5ce7' }}>Manage Blueprints</Link>
          <Link to="/compare" className="btn btn-primary" style={{ background: '#0984e3' }}>Compare Images</Link>
          <Link to="/history" className="btn btn-primary" style={{ background: '#636e72' }}>Build History</Link>
          <Link to="/channels" className="btn btn-primary" style={{ background: '#00cec9' }}>Channels</Link>
          <Link to="/policies" className="btn btn-primary" style={{ background: '#e17055' }}>Policies</Link>
          <Link to="/fleet" className="btn btn-primary" style={{ background: '#fdcb6e', color: '#2d3436' }}>Fleet</Link>
        </div>
      </div>
    </div>
  );
}
